import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  useCreateEntraConnectionMutation,
  useListEntraConnectionsQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { defaultDepartment } from "../../../../common/departments/defaultDepartment";
import { useApiError } from "../../../../common/errors/useApiError";
import { newConnectionDefaultRoleScope } from "./entraDefaultRoleScope";
import { EntraTenantCard } from "./EntraTenantCard";

export const EntraConnections = () => {
  const authState = useLoggedInWithOrgContextState();
  const organizationId = authState.selectedOrg.id;
  const departments = authState.selectedOrg.departments;
  const { trigger } = useApiError();

  const { data, isLoading } = useListEntraConnectionsQuery({
    orgId: organizationId,
  });
  const [createConnection, { isLoading: isCreating }] =
    useCreateEntraConnectionMutation();

  const [isAdding, setIsAdding] = useState(false);
  const [tenantId, setTenantId] = useState("");

  const add = async () => {
    try {
      await createConnection({
        orgId: organizationId,
        createEntraConnectionRequest: {
          tenantId: tenantId.trim(),
          defaultRoleScope: newConnectionDefaultRoleScope(authState.selectedOrg.subscription),
        },
      }).unwrap();
      setTenantId("");
      setIsAdding(false);
    } catch (e) {
      trigger(e);
    }
  };

  if (isLoading)
    return (
      <Spinner
        color="smPrimary"
        thickness="0.2rem"
        emptyColor="smBorder"
        mt="5"
      />
    );

  const connections = data?.connections ?? [];

  return (
    <>
      {connections.length === 0 && !isAdding && (
        <Alert status="info" variant="subtle" borderRadius="md" mt="5">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {t(
              "No Entra tenant is connected yet. Connect one to let Entra ID manage who has access",
            )}
            .
          </AlertDescription>
        </Alert>
      )}

      {connections.map((connection) => (
        <EntraTenantCard
          key={connection.id}
          organizationId={organizationId}
          connection={connection}
          departments={departments}
          defaultDepartmentName={defaultDepartment(authState.selectedOrg)?.name ?? ""}
          subscription={authState.selectedOrg.subscription}
        />
      ))}

      {isAdding ? (
        <Box
          mt="5"
          width="full"
          rounded="lg"
          border="1px solid"
          borderColor="smBorder"
          overflow="hidden"
        >
          <Flex
            alignItems="center"
            gap="3"
            px="5"
            py="3"
            backgroundColor="smBackgroundSecondary"
            borderBottom="1px solid"
            borderColor="smBorder"
          >
            <Text fontWeight="semibold">{t("Connect an Entra tenant")}</Text>
          </Flex>

          <Flex px="5" py="4" flexDir="column" gap="3">
            <FormControl maxW="md">
              <FormLabel fontSize="xs" color="smMuted" mb="1">
                {t("Tenant ID")}
              </FormLabel>
              <Input
                size="sm"
                fontFamily="mono"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                autoFocus
              />
              <FormHelperText fontSize="xs">
                {t(
                  "You find the tenant ID in the Azure portal, under Entra ID overview",
                )}
                .
              </FormHelperText>
            </FormControl>
            <Flex gap="2">
              <Button
                size="sm"
                colorScheme="teal"
                isDisabled={!tenantId.trim() || isCreating}
                onClick={() => void add()}
              >
                {isCreating ? <Spinner size="sm" /> : t("Connect")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAdding(false);
                  setTenantId("");
                }}
              >
                {t("Cancel")}
              </Button>
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Flex mt="5">
          <Button
            size="sm"
            variant="outline"
            colorScheme="teal"
            leftIcon={<FaPlus />}
            onClick={() => setIsAdding(true)}
          >
            {t("Connect an Entra tenant")}
          </Button>
        </Flex>
      )}
    </>
  );
};
